import React, { useState, useEffect } from "react";
import { MapPin, TrendingUp, Droplets, History, FileDown, ArrowRight, BadgeCheck, Loader2, Sparkles, Search, Home } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { submitValuation, downloadValuationReport, valuationLookup } from "../api";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useGoogleReady } from "../lib/gmaps";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const TYPES = ["Apartment", "Terraced House", "Semi-Detached House", "Detached House", "Town House"];
const FINISHES = [
  { value: "renovated", label: "Renovated / high spec" },
  { value: "average", label: "Average condition" },
  { value: "needs_work", label: "Needs work / dated" },
];

const SUBJECT_PIN = "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='46' height='58' viewBox='0 0 44 54'>
     <path d='M22 0C10 0 0 9.4 0 21c0 15 22 33 22 33s22-18 22-33C44 9.4 34 0 22 0z' fill='#1c1c1e' stroke='#b8935f' stroke-width='3'/>
     <path d='M22 9 L33 19 L30 19 L30 31 L25 31 L25 23 L19 23 L19 31 L14 31 L14 19 L11 19 Z' fill='#b8935f'/>
   </svg>`
);

const CompsMap = ({ center, comps, subjectQuery }) => {
  const ready = useGoogleReady();
  const [subject, setSubject] = useState(null);
  const withCoords = comps.filter((c) => c.lat && c.lng);

  useEffect(() => {
    if (!ready || !subjectQuery || !window.google) return;
    let cancelled = false;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: `${subjectQuery}, UK` }, (res, status) => {
      if (!cancelled && status === "OK" && res?.[0]) {
        const loc = res[0].geometry.location;
        setSubject({ lat: loc.lat(), lng: loc.lng() });
      }
    });
    return () => { cancelled = true; };
  }, [ready, subjectQuery]);

  if (!ready || (!center && !subject)) return null;
  const g = window.google;
  const mapCenter = subject || center;
  const dot = (color, size) => (g ? {
    path: g.maps.SymbolPath.CIRCLE, scale: size, fillColor: color, fillOpacity: 1,
    strokeColor: "#ffffff", strokeWeight: 2,
  } : undefined);
  return (
    <div className="mt-5 rounded-xl overflow-hidden border border-[#f0ece5]" data-testid="comps-map">
      <div className="flex items-center justify-between px-3 pt-3 pb-2">
        <p className="text-[12px] tracking-[0.12em] uppercase text-[#b8935f] flex items-center gap-1.5"><MapPin size={13} /> Comparable sales nearby</p>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-[#4a4a4a] flex items-center gap-1"><Home size={12} className="text-[#1c1c1e]" /> Your property</span>
          <span className="text-[11px] text-[#8a8a8a] flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#b8935f]" /> 3 closest</span>
        </div>
      </div>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "260px" }}
        center={mapCenter}
        zoom={15}
        options={{ disableDefaultUI: true, zoomControl: true, gestureHandling: "cooperative" }}
      >
        {subject && (
          <Marker
            position={subject}
            title={`Your property — ${subjectQuery}`}
            icon={{ url: SUBJECT_PIN, scaledSize: new g.maps.Size(46, 58), anchor: new g.maps.Point(23, 58) }}
            zIndex={999}
            data-testid="subject-pin"
          />
        )}
        {withCoords.map((c, i) => {
          const closest = i < 3;
          return (
            <Marker
              key={i}
              position={{ lat: c.lat, lng: c.lng }}
              title={`${closest ? "★ Closest · " : ""}${c.address} — £${Number(c.price).toLocaleString()}${c.date ? ` (${c.date})` : ""}`}
              icon={dot(closest ? "#b8935f" : "#9aa0a6", closest ? 9 : 6)}
              zIndex={closest ? 10 : 1}
            />
          );
        })}
      </GoogleMap>
      <div className="px-3 py-2 text-[11px] text-[#8a8a8a] bg-[#faf7f2] border-t border-[#f0ece5]">
        The dark pin marks your property; the 3 gold dots are the closest recent sales.
      </div>
    </div>
  );
};

const TrendChart = ({ trend }) => {
  const data = trend.map((t) => ({ year: String(t.year), value: t.avg }));
  const change = data.length > 1 && data[0].value ? Math.round((data[data.length - 1].value - data[0].value) / data[0].value * 100) : 0;
  return (
    <div className="mt-5 bg-white rounded-xl border border-[#f0ece5] p-4" data-testid="price-trend-chart">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[12px] tracking-[0.12em] uppercase text-[#b8935f] flex items-center gap-1.5"><TrendingUp size={13} /> Local price trend</p>
        <span className={`text-[11px] px-2 py-0.5 rounded-full font-500 ${change >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>{change >= 0 ? "+" : ""}{change}% ({data[0].year}–{data[data.length - 1].year})</span>
      </div>
      <ResponsiveContainer width="100%" height={170}>
        <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0ece5" vertical={false} />
          <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#8a8a8a" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: "#8a8a8a" }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
          <Tooltip formatter={(v) => [`£${Number(v).toLocaleString()}`, "Avg sold"]} contentStyle={{ borderRadius: 8, border: "1px solid #f0ece5", fontSize: 12 }} />
          <Line type="monotone" dataKey="value" stroke="#b8935f" strokeWidth={2.5} dot={{ r: 3.5, fill: "#b8935f" }} activeDot={{ r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
      <p className="text-[11px] text-[#a0a0a0] mt-1">Average local sold price by year · PropertyData / HM Land Registry</p>
    </div>
  );
};

const LOADING_STEPS = [
  "Crunching live Bath & Bristol sold data…",
  "Checking HM Land Registry comparables…",
  "Reading EPC floor areas for your street…",
  "Running the PropertyData valuation model…",
  "Finalising your estimate…",
];

const ValuationLoader = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % LOADING_STEPS.length), 1600);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="bg-[#faf7f2] rounded-2xl border border-[#f0ece5] p-8 text-center fade-up" data-testid="valuation-loading">
      <div className="relative w-16 h-16 mx-auto mb-5">
        <div className="absolute inset-0 rounded-full border-2 border-[#e6dcc9]" />
        <div className="absolute inset-0 rounded-full border-2 border-[#b8935f] border-t-transparent animate-spin" />
        <Sparkles size={22} className="text-[#b8935f] absolute inset-0 m-auto" />
      </div>
      <p className="font-display text-[19px] text-[#2b2b2b] mb-2">Valuing your property</p>
      <p className="text-[14px] text-[#8a8a8a] transition-opacity duration-300 min-h-[20px]" key={i}>{LOADING_STEPS[i]}</p>
      <div className="flex items-center justify-center gap-1.5 mt-4">
        {LOADING_STEPS.map((_, idx) => (
          <span key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === i ? "w-6 bg-[#b8935f]" : "w-1.5 bg-[#e2d8c8]"}`} />
        ))}
      </div>
      <p className="text-[12px] text-[#a0a0a0] mt-5">Querying three live data sources — this takes a few seconds for a truly accurate figure.</p>
    </div>
  );
};

const ValuationReport = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ postcode: "", address: "", propertyType: "Terraced House", bedrooms: 3, bathrooms: 1, constructionEra: "pre_1914", finish: "average", sqft: "", name: "", email: "", phone: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [lookupBusy, setLookupBusy] = useState(false);

  const fmt = (n) => `\u00A3${Math.round(n).toLocaleString()}`;

  const findAddresses = async () => {
    const pc = form.postcode.trim();
    if (pc.length < 5) { toast({ title: "Enter a full postcode", description: "e.g. BA2 5DZ" }); return; }
    setLookupBusy(true);
    try {
      const d = await valuationLookup(pc);
      setAddresses(d.addresses || []);
      if (!d.addresses?.length) toast({ title: "No addresses found", description: "You can still value by postcode below." });
    } catch { toast({ title: "Lookup failed", description: "You can still value by postcode." }); }
    finally { setLookupBusy(false); }
  };

  const pickAddress = (addr) => {
    const match = addresses.find((a) => a.address === addr);
    setForm((f) => ({ ...f, address: addr, sqft: match?.sqft ? String(match.sqft) : f.sqft }));
    if (match?.sqft) toast({ title: "Size detected", description: `~${Number(match.sqft).toLocaleString()} sq ft from EPC records — edit if needed.` });
  };

  const handleStart = async (e) => {
    e.preventDefault();
    if (!form.postcode.trim()) {
      toast({ title: "Please enter a postcode", description: "We need your postcode to begin the valuation." });
      return;
    }
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast({ title: "Please add your details", description: "We need your name, email and phone to prepare your valuation." });
      return;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      toast({ title: "Check your email", description: "Please enter a valid email address." });
      return;
    }
    if (!form.consent) {
      toast({ title: "Please confirm consent", description: "Tick the box so we can contact you about your valuation." });
      return;
    }
    setLoading(true);
    try {
      const data = await submitValuation({ ...form, sqft: form.sqft ? Number(form.sqft) : null, type: "instant" });
      setResult(data);
      toast({ title: "Valuation ready", description: `Estimated value for ${form.address || form.postcode.toUpperCase()}.` });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  const handlePdf = async () => {
    try {
      await downloadValuationReport(form);
      toast({ title: "Report downloading", description: "Your branded valuation report is on its way." });
    } catch {
      toast({ title: "Couldn't generate report", description: "Please try again." });
    }
  };

  const features = [
    { icon: TrendingUp, label: "Estimated value" },
    { icon: Droplets, label: "Flood risk" },
    { icon: History, label: "Sale history" },
  ];

  return (
    <section id="valuation" className="bg-white py-24">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1740596400206-f894025300b5"
              alt="Instant valuation report"
              className="rounded-2xl w-full h-[440px] object-cover shadow-[0_20px_60px_rgba(0,0,0,0.15)]"
            />
            <div className="absolute -bottom-6 -right-4 sm:right-6 bg-white rounded-xl shadow-[0_12px_40px_rgba(0,0,0,0.15)] p-5 border border-[#f0ece5]">
              <p className="text-[11px] tracking-[0.15em] uppercase text-[#b8935f] mb-1">Instant valuation report</p>
              <p className="font-display text-[28px] font-500 text-[#2b2b2b]">In 60 seconds</p>
            </div>
          </div>

          <div>
            <p className="text-[12px] tracking-[0.28em] uppercase text-[#b8935f] mb-3">Free Property Valuation</p>
            <h2 className="font-display text-[32px] md:text-[42px] font-300 text-[#2b2b2b] leading-tight mb-5">
              Discover how much your property is worth and more
            </h2>
            <p className="text-[15px] text-[#6a6a6a] leading-relaxed mb-8">
              Find out how much your home could be worth and uncover key insights about your
              property with our free, no-obligation valuation report in Bath &amp; Bristol.
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              {features.map((f) => (
                <span key={f.label} className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#faf7f2] text-[13px] text-[#4a4a4a] border border-[#f0ece5]">
                  <f.icon size={15} className="text-[#b8935f]" /> {f.label}
                </span>
              ))}
            </div>

            {result ? (
              <div className="bg-[#faf7f2] rounded-2xl border border-[#f0ece5] p-7 fade-up" data-testid="valuation-result">
                <p className="text-[12px] tracking-[0.15em] uppercase text-[#b8935f] mb-1">Estimated Market Value</p>
                <p className="font-display text-[40px] font-500 text-[#2b2b2b] leading-none">{fmt(result.estimate)}</p>
                <p className="text-[14px] text-[#6a6a6a] mt-2">Range: {fmt(result.low)} — {fmt(result.high)}</p>
                <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="val-confidence">
                  {result.confidence && (
                    <span className={`text-[11px] px-2.5 py-1 rounded-full capitalize font-500 ${result.confidence === "high" ? "bg-green-100 text-green-700" : result.confidence === "medium" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{result.confidence} confidence</span>
                  )}
                  {result.localSalesAnalysed > 0 && (
                    <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#faf7f2] border border-[#f0ece5] text-[#6a6a6a]">Based on {result.localSalesAnalysed} local sales</span>
                  )}
                  {result.internalArea && (
                    <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#faf7f2] border border-[#f0ece5] text-[#6a6a6a]">~{Number(result.internalArea).toLocaleString()} sq ft{result.areaSource ? ` · ${result.areaSource}` : ""}</span>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2 mt-5">
                  <div className="bg-white rounded-lg border border-[#f0ece5] p-3 text-center">
                    <Droplets size={16} className="text-[#b8935f] mx-auto mb-1" />
                    <p className="text-[11px] text-[#a0a0a0] uppercase">Flood risk</p>
                    <p className="text-[14px] font-500 text-[#2b2b2b]" data-testid="val-flood">{result.floodRisk || "Low"}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-[#f0ece5] p-3 text-center">
                    <TrendingUp size={16} className="text-[#b8935f] mx-auto mb-1" />
                    <p className="text-[11px] text-[#a0a0a0] uppercase">12m growth</p>
                    <p className="text-[14px] font-500 text-green-600">+{result.growth12m ?? "—"}%</p>
                  </div>
                  <div className="bg-white rounded-lg border border-[#f0ece5] p-3 text-center">
                    <History size={16} className="text-[#b8935f] mx-auto mb-1" />
                    <p className="text-[11px] text-[#a0a0a0] uppercase">Avg days</p>
                    <p className="text-[14px] font-500 text-[#2b2b2b]">{result.avgDaysOnMarket ?? "—"}</p>
                  </div>
                </div>

                {result.saleHistory?.length > 0 && (
                  <div className="mt-5" data-testid="val-sale-history">
                    <p className="text-[12px] tracking-[0.12em] uppercase text-[#b8935f] mb-2 flex items-center gap-1.5"><History size={13} /> Sale history</p>
                    <div className="space-y-1">
                      {result.saleHistory.map((h, i) => (
                        <div key={i} className="flex justify-between text-[13px] text-[#4a4a4a]"><span>{h.date}</span><span className="font-500">{fmt(h.price)}</span></div>
                      ))}
                    </div>
                  </div>
                )}

                {result.avgAreaPrice ? (
                  <div className="mt-4 flex items-center justify-between bg-white rounded-lg border border-[#f0ece5] p-3" data-testid="val-area-price">
                    <span className="text-[12px] text-[#a0a0a0] uppercase tracking-wide">Area average (postcode)</span>
                    <span className="text-[15px] font-500 text-[#2b2b2b]">{fmt(result.avgAreaPrice)}</span>
                  </div>
                ) : null}

                {result.priceTrend?.length >= 2 && <TrendChart trend={result.priceTrend} />}

                {result.comparables?.length > 0 && (
                  <div className="mt-5">
                    <p className="text-[12px] tracking-[0.12em] uppercase text-[#b8935f] mb-2">Comparable recent sales{result.localSalesAnalysed ? ` · ${result.localSalesAnalysed} analysed` : ""}</p>
                    <div className="space-y-1">
                      {result.comparables.slice(0, 6).map((c, i) => (
                        <div key={i} className="flex justify-between text-[13px] text-[#4a4a4a]"><span className="truncate pr-2">{c.address}{c.beds ? ` · ${c.beds} bed` : ""}</span><span className="font-500 whitespace-nowrap">{fmt(c.price)}{c.date ? ` · ${c.date}` : ""}</span></div>
                      ))}
                    </div>
                    <CompsMap center={result.compCenter} comps={result.comparables} subjectQuery={form.address || form.postcode} />
                  </div>
                )}

                {result.dataSources?.length > 0 && (
                  <p className="text-[11px] text-[#a0a0a0] mt-4 flex items-center gap-1.5" data-testid="val-data-sources"><BadgeCheck size={12} className="text-[#b8935f]" /> Powered by {result.dataSources.join(", ")}</p>
                )}

                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <button onClick={handlePdf} data-testid="download-report-btn" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors">
                    <FileDown size={16} /> Download PDF Report
                  </button>
                  <button onClick={() => setResult(null)} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-[#e2d8c8] text-[#6a6a6a] text-[14px] font-500 hover:border-[#b8935f] hover:text-[#b8935f] transition-colors">
                    New Valuation
                  </button>
                </div>
                <p className="text-[12px] text-[#a0a0a0] mt-4">Automated estimate, not a formal RICS valuation. Book a free home visit for an accurate figure.</p>
              </div>
            ) : loading ? (
              <ValuationLoader />
            ) : (
              <form onSubmit={handleStart} className="space-y-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b8935f]" />
                    <input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); findAddresses(); } }} placeholder="Enter your postcode (e.g. BA2 5DZ)" data-testid="address-autocomplete-input" className="w-full h-[54px] pl-11 pr-4 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition" />
                  </div>
                  <button type="button" onClick={findAddresses} disabled={lookupBusy} data-testid="find-address-btn" className="shrink-0 px-5 h-[54px] rounded-lg bg-[#1c1c1e] text-white text-[14px] font-500 hover:bg-black transition-colors disabled:opacity-60 inline-flex items-center gap-2">{lookupBusy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Find address</button>
                </div>
                {addresses.length > 0 && (
                  <Select value={form.address} onValueChange={pickAddress}>
                    <SelectTrigger className="h-[54px] border-[#e2d8c8]" data-testid="val-address-select"><SelectValue placeholder={`Select your address (${addresses.length} found)`} /></SelectTrigger>
                    <SelectContent className="max-h-[260px]">{addresses.map((a) => <SelectItem key={a.address} value={a.address}>{a.address}{a.sqft ? ` · ${Number(a.sqft).toLocaleString()} sq ft` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {form.address && <p className="text-[12px] text-[#6a6a6a] -mt-1 pl-1" data-testid="val-selected-address"><BadgeCheck size={12} className="inline text-[#b8935f]" /> {form.address}</p>}
                <div className="grid grid-cols-2 gap-3">
                  <Select value={form.propertyType} onValueChange={(v) => setForm({ ...form, propertyType: v })}>
                    <SelectTrigger className="h-[54px] border-[#e2d8c8]" data-testid="val-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={String(form.bedrooms)} onValueChange={(v) => setForm({ ...form, bedrooms: Number(v) })}>
                    <SelectTrigger className="h-[54px] border-[#e2d8c8]" data-testid="val-beds"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 5].map((b) => <SelectItem key={b} value={String(b)}>{`${b} bedroom${b > 1 ? "s" : ""}`}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Select value={String(form.bathrooms)} onValueChange={(v) => setForm({ ...form, bathrooms: Number(v) })}>
                    <SelectTrigger className="h-[54px] border-[#e2d8c8]" data-testid="val-baths"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 5].map((b) => <SelectItem key={b} value={String(b)}>{`${b} bathroom${b > 1 ? "s" : ""}`}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={form.finish} onValueChange={(v) => setForm({ ...form, finish: v })}>
                    <SelectTrigger className="h-[54px] border-[#e2d8c8]" data-testid="val-finish"><SelectValue /></SelectTrigger>
                    <SelectContent>{FINISHES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <input value={form.sqft} onChange={(e) => setForm({ ...form, sqft: e.target.value.replace(/[^0-9]/g, "") })} placeholder="Size sq ft" className="w-full h-[54px] px-4 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition" data-testid="val-sqft" />
                    {form.sqft && form.address && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded">auto</span>}
                  </div>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" data-testid="val-name" className="w-full h-[54px] px-4 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" type="email" data-testid="val-email" className="w-full h-[54px] px-4 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition" />
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" type="tel" data-testid="val-phone" className="w-full h-[54px] px-4 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition" />
                </div>
                <label className="flex items-start gap-2.5 text-[12px] text-[#6a6a6a] leading-relaxed cursor-pointer pl-1" data-testid="val-consent-label">
                  <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="val-consent" className="mt-0.5 accent-[#b8935f] w-4 h-4 shrink-0" />
                  <span>I agree that Anadear Estates may contact me about my valuation and store my details in line with the <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-[#b8935f] underline">Privacy Policy</a>.</span>
                </label>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-[54px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {loading ? "Calculating..." : <>Start my valuation <ArrowRight size={17} /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValuationReport;
